import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import config from '../../config'
import * as Api from "../../common/Api";
import { assign, reduce, remove } from "lodash";

const initialState = {
    loading: false,
    reload: false,
    alertMessage: undefined,
    successMessage: undefined,
    managedSource: null,
    sourceData: null,
    sourcePreview: null,
    inputSchemas: [],
    mappedSelectedFileFields: [],
    s3Files: [],
    previewFromS3: [],
    // TODO: GET RID OF ONCE WE REVISIT EDIT
    mappedToAttributes: []
};

export const getInputSchemas = createAsyncThunk('inputs/schemas', async (filter) => {
    const SCHEMAS_URL = `${config.services.ATLAS.apiUrl}/managed-inputs/schemas`;
    const schemas = await Api.get(true, SCHEMAS_URL, filter);
    return schemas;
});

export const browseS3ForSourceData = createAsyncThunk('sources/browse-s3-for-source-data', async (data) => {
    const { dataType, s3BaseKey } = data;
    // Split s3BaseKey into bucket and path
    const [bucket, ...pathParts] = s3BaseKey.split('/');
    let basePath = pathParts.join('/');

    // Remove leading slash if present
    basePath = basePath.replace(/^\//, '');

    // Add trailing slash if not present 
    if (!basePath.endsWith('/')) {
        basePath += '/';
    }
    const SOURCE_DATA_BROWSE_S3_URL = `${config.services.ATLAS.apiUrl}/managed-source-data/browse-s3`;
    const response = await Api.post(
        true, SOURCE_DATA_BROWSE_S3_URL,
        {
            options: {
                test: null
            },
            data: {
                bucket: bucket,
                prefix: basePath,
                dataType: dataType,
            }
        });
    return response;
});

export const previewDataOnS3ForSourceData = createAsyncThunk('sources/preview-data-on-s3-for-source-data', async (data) => {
    const { s3BaseKey, prefix, dataType } = data;

    // Split s3BaseKey into bucket and path
    const [bucket, ...pathParts] = s3BaseKey.split('/');

    const SOURCE_DATA_PREVIEW_S3_URL = `${config.services.ATLAS.apiUrl}/managed-source-data/preview-s3`;
    const response = await Api.post(
        true, SOURCE_DATA_PREVIEW_S3_URL,
        {
            options: {
                test: null
            },
            data: {
                bucket: bucket,
                prefix: prefix,
                dataType: dataType,
            }
        });
    return response;
});

export const getSourceById = createAsyncThunk('sources/getSourceById', async (id) => {
    const GET_MANAGED_SOURCE_BY_ID_URL = `${config.services.ATLAS.apiUrl}/managed-sources/${id}`;
    const source = await Api.get(true, GET_MANAGED_SOURCE_BY_ID_URL);
    return source;
});

export const getSourceDataBySourceId = createAsyncThunk('sources/getSourceData', async (sourceId) => {
    const GET_SOURCE_DATA_URL = `${config.services.ATLAS.apiUrl}/managed-source-data`;
    const query = {
        filter: {
            where: {
                and: [
                    {
                        sourceId: sourceId,
                    },
                    {
                        current: true,
                    }
                ]
            },
        }
    };
    const sourceData = await Api.get(true, GET_SOURCE_DATA_URL, query);
    
    // API can return array or single object - handle both cases
    if (Array.isArray(sourceData) && sourceData.length > 0) {
        return sourceData[0];
    }
    return sourceData;
});

export const getSourcePreviewBySourceId = createAsyncThunk('sources/getSourcePreview', async (sourceId) => {
    const GET_SOURCE_PREVIEW_URL = `${config.services.ATLAS.apiUrl}/managed-source-data/preview`;
    const query = {
        filter: {
            where: {
                and: [
                    {
                        sourceId: sourceId,
                    },
                    {
                        current: true,
                    }
                ]
            },
        }
    };
    const previewData = await Api.get(true, GET_SOURCE_PREVIEW_URL, query);
    
    // API returns array - take first result if available
    if (Array.isArray(previewData) && previewData.length > 0) {
        return previewData[0];
    }
    return previewData;
});

export const createSource = createAsyncThunk(
    'sources/create-source',
    async (data, { rejectWithValue }) => {

        const { selectedInputSchema, selectedFile, selectedFileFields, mappedSelectedFileFields, source, sourceData } = data;
        try {

            // Create Source
            const sourceToCreate = assign({}, source);
            const CREATE_SOURCE_URL = `${config.services.ATLAS.apiUrl}/managed-sources`;
            const createdSource = await Api.post(true,
                CREATE_SOURCE_URL,
                {
                    options: {
                        test: false
                    },
                    data: sourceToCreate
                }
            );
            console.log(`source [${createdSource.id}] created`);

            // If selectedFile and channel is upload, upload the data
            if (selectedFile && sourceData.channel === 'upload') {
                const GET_PRESIGNED_UPLOAD_URL = `${config.services.ATLAS.apiUrl}/managed-sources/${createdSource.id}/upload-source-data`;
                const preSignedUploadBody = {
                    options: null,
                    data: {
                        fileName: selectedFile.name
                    }
                }
                const preSignedUploadUrl = await Api.post(true, GET_PRESIGNED_UPLOAD_URL, preSignedUploadBody);
                // Upload to S3
                const uploadResult = await fetch(preSignedUploadUrl.uploadUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'text/plain' },
                    body: selectedFile
                })
                console.log(`file [${selectedFile.name}] uploaded to source [${createdSource.id}]`);

                // Create Source Data
                const sourceDataToCreate = assign({}, sourceData, {
                    sourceId: createdSource.id,
                    fields: selectedFileFields.map((field, idx) => {
                        return {
                            originalName: field,
                            sanitizedName: field,
                            ordinalPosition: idx
                        }
                    }),
                    ref: preSignedUploadUrl.uploadRef,
                    // legacy nomenclature
                    inputMappingId: selectedInputSchema.id,
                    inputMapping: {
                        fields: selectedFileFields.map((field, idx) => `${field}_${idx}`),
                        attributes: mappedSelectedFileFields.reduce((acc, m) => ({ ...acc, [m.property]: m.selectedFileField }), {}),
                        inputMappingId: selectedInputSchema.id
                    }
                });
                const CREATE_SOURCE_DATA_URL = `${config.services.ATLAS.apiUrl}/managed-source-data`;
                const createdSourceData = await Api.post(true,
                    CREATE_SOURCE_DATA_URL,
                    {
                        options: {
                            test: false
                        },
                        data: sourceDataToCreate
                    }
                );
                console.log(`source data [${createdSourceData.id}] for ref [${sourceDataToCreate.ref}] created for source [${createdSource.id}]`);
            } else if (sourceData.channel === 's3') {
                // Create Source Data
                const sourceDataToCreate = assign({}, sourceData, {
                    sourceId: createdSource.id,
                    fields: selectedFileFields.map((field, idx) => {
                        return {
                            originalName: field,
                            sanitizedName: field,
                            ordinalPosition: idx
                        }
                    }),
                    // TODO: WE NEED TO SOLVE FOR THIS! IT DOES NOT INCLUDE THE BUCKET AND PATH!
                    ref: sourceData.fileName, 
                    // legacy nomenclature
                    inputMappingId: selectedInputSchema.id,
                    inputMapping: {
                        fields: selectedFileFields.map((field, idx) => `${field}_${idx}`),
                        attributes: mappedSelectedFileFields.reduce((acc, m) => ({ ...acc, [m.property]: m.selectedFileField }), {}),
                        inputMappingId: selectedInputSchema.id
                    }
                });
                const CREATE_SOURCE_DATA_URL = `${config.services.ATLAS.apiUrl}/managed-source-data`;
                const createdSourceData = await Api.post(true,
                    CREATE_SOURCE_DATA_URL,
                    {
                        options: {
                            test: false
                        },
                        data: sourceDataToCreate
                    }
                );
                console.log(`source data [${createdSourceData.id}] for ref [${sourceDataToCreate.ref}] created for source [${createdSource.id}]`);
            }
            return createdSource;

        } catch (err) {
            return rejectWithValue({
                message: err.message || 'An error occurred',
                status: err.status,
                response: err.response ? {
                    body: err.response.body,
                    text: err.response.text,
                    status: err.response.status,
                    statusType: err.response.statusType
                } : null,
                type: err.type,
                originalError: err
            });
        }
    });

export const editSource = createAsyncThunk('sources/editSource', async ({ id, data }, { rejectWithValue }) => {
    const { selectedInputSchema, selectedFile, selectedFileFields, mappedSelectedFileFields, source, sourceData, isNewFileUploaded } = data;
    
    try {
        // Update Source
        const EDIT_SOURCE_URL = `${config.services.ATLAS.apiUrl}/managed-sources/${id}/edit`;
        const sourceToUpdate = assign({}, source);
        const updatedSource = await Api.post(true, EDIT_SOURCE_URL, {
            data: sourceToUpdate
        });
        console.log(`source [${updatedSource.id}] updated`);
        
        // If a new file was uploaded (tracked by explicit flag from form)
        if (isNewFileUploaded) {
            
            // Handle file upload channel
            if (sourceData.channel === 'upload') {
                const GET_PRESIGNED_UPLOAD_URL = `${config.services.ATLAS.apiUrl}/managed-sources/${updatedSource.id}/upload-source-data`;
                const preSignedUploadBody = {
                    options: null,
                    data: {
                        fileName: selectedFile.name
                    }
                };
                const preSignedUploadUrl = await Api.post(true, GET_PRESIGNED_UPLOAD_URL, preSignedUploadBody);
                
                // Upload to S3
                await fetch(preSignedUploadUrl.uploadUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'text/plain' },
                    body: selectedFile
                });
                console.log(`file [${selectedFile.name}] uploaded to source [${updatedSource.id}]`);

                // Create/Update Source Data with new file
                const sourceDataToUpdate = assign({}, sourceData, {
                    sourceId: updatedSource.id,
                    fields: selectedFileFields.map((field, idx) => ({
                        originalName: field,
                        sanitizedName: field,
                        ordinalPosition: idx
                    })),
                    ref: preSignedUploadUrl.uploadRef,
                    inputMappingId: selectedInputSchema.id,
                    inputMapping: {
                        fields: selectedFileFields.map((field, idx) => `${field}_${idx}`),
                        attributes: mappedSelectedFileFields.reduce((acc, m) => ({ ...acc, [m.property]: m.selectedFileField }), {}),
                        inputMappingId: selectedInputSchema.id
                    }
                });
                
                const CREATE_SOURCE_DATA_URL = `${config.services.ATLAS.apiUrl}/managed-source-data`;
                const createdSourceData = await Api.post(true, CREATE_SOURCE_DATA_URL, {
                    options: { test: false },
                    data: sourceDataToUpdate
                });
                console.log(`source data [${createdSourceData.id}] created for source [${updatedSource.id}]`);
                
            } else if (sourceData.channel === 's3') {
                // Handle S3 channel
                const sourceDataToUpdate = assign({}, sourceData, {
                    sourceId: updatedSource.id,
                    fields: selectedFileFields.map((field, idx) => ({
                        originalName: field,
                        sanitizedName: field,
                        ordinalPosition: idx
                    })),
                    ref: sourceData.fileName,
                    inputMappingId: selectedInputSchema.id,
                    inputMapping: {
                        fields: selectedFileFields.map((field, idx) => `${field}_${idx}`),
                        attributes: mappedSelectedFileFields.reduce((acc, m) => ({ ...acc, [m.property]: m.selectedFileField }), {}),
                        inputMappingId: selectedInputSchema.id
                    }
                });
                
                const CREATE_SOURCE_DATA_URL = `${config.services.ATLAS.apiUrl}/managed-source-data`;
                const createdSourceData = await Api.post(true, CREATE_SOURCE_DATA_URL, {
                    options: { test: false },
                    data: sourceDataToUpdate
                });
                console.log(`source data [${createdSourceData.id}] created for source [${updatedSource.id}]`);
            }
        } else {
            // No new file uploaded - just update mappings if they changed
            // This updates the existing sourceData with potentially changed mappings
            if (mappedSelectedFileFields && mappedSelectedFileFields.length > 0) {
                const sourceDataToUpdate = assign({}, sourceData, {
                    sourceId: updatedSource.id,
                    fields: selectedFileFields.map((field, idx) => ({
                        originalName: field,
                        sanitizedName: field,
                        ordinalPosition: idx
                    })),
                    inputMappingId: selectedInputSchema.id,
                    inputMapping: {
                        fields: selectedFileFields.map((field, idx) => `${field}_${idx}`),
                        attributes: mappedSelectedFileFields.reduce((acc, m) => ({ ...acc, [m.property]: m.selectedFileField }), {}),
                        inputMappingId: selectedInputSchema.id
                    }
                });
                
                const CREATE_SOURCE_DATA_URL = `${config.services.ATLAS.apiUrl}/managed-source-data`;
                const createdSourceData = await Api.post(true, CREATE_SOURCE_DATA_URL, {
                    options: { test: false },
                    data: sourceDataToUpdate
                });
                console.log(`source data [${createdSourceData.id}] updated for source [${updatedSource.id}]`);
            }
        }

        return updatedSource;
    } catch (err) {
        console.error('Error editing source:', err);
        return rejectWithValue(err.message);
    }
});

const sourceSlice = createSlice({
    name: 'sourceSlice', initialState,
    reducers: {
        clearState: (state, action) => {
            state.loading = false;
            state.reload = false;
            state.alertMessage = undefined;
            state.successMessage = undefined;
            state.managedSource = null;
            state.inputSchemas = [];
            // Only clear mappedSelectedFileFields if explicitly requested
            if (action.payload && action.payload.clearMappings) {
                state.mappedSelectedFileFields = [];
            }
            state.s3Files = [];
            state.previewFromS3 = [];
            return state;
        },
        updateMappedSelectedFileFields: {
            reducer(state, action) {
                const prop = action.payload;
                remove(state.mappedSelectedFileFields, (o) => {
                    return o.property === prop.property
                });
                state.mappedSelectedFileFields.push(prop);
            }
        },
        updateAllMappedSelectedFileFields: {
            reducer(state, action) {
                state.mappedSelectedFileFields = action.payload;
            }
        },
        updateMappedToAttributes: {
            reducer(state, action) {
                const attr = action.payload;
                remove(state.mappedToAttributes, (o) => {
                    return o.attribute === attr.attribute
                });
                state.mappedToAttributes.push(attr);
            }
        },
        updateAllMappedToAttributes: {
            reducer(state, action) {
                state.mappedToAttributes = action.payload;
            }
        },
        clearS3Files: (state, action) => {
            state.s3Files = [];
            state.previewFromS3 = [];
            // Only clear mappedSelectedFileFields if explicitly requested
            if (action.payload && action.payload.clearMappings) {
                state.mappedSelectedFileFields = [];
            }
            return state;
        },
        clearUploadFiles: (state, action) => {
            // TODO: Rest of state is local? Should we change it?
            // Only clear mappedSelectedFileFields if explicitly requested
            if (action.payload && action.payload.clearMappings) {
                state.mappedSelectedFileFields = [];
            }
            return state;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getSourceById.pending, (state, action) => {
                state.loading = true;
                state.successMessage = undefined;
                state.alertMessage = undefined;
            })
            .addCase(getSourceById.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.managedSource = action.payload;
                }
            })
            .addCase(getSourceById.rejected, (state, action) => {
                state.loading = false;
                state.alertMessage = 'Unable to retrieve the source';
            })
            .addCase(getSourceDataBySourceId.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(getSourceDataBySourceId.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.sourceData = action.payload;
                }
            })
            .addCase(getSourceDataBySourceId.rejected, (state, action) => {
                state.loading = false;
                // Don't set alert - sourceData might not exist for all sources
                console.warn('Unable to retrieve source data:', action.error);
            })
            .addCase(getSourcePreviewBySourceId.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(getSourcePreviewBySourceId.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.sourcePreview = action.payload;
                }
            })
            .addCase(getSourcePreviewBySourceId.rejected, (state, action) => {
                state.loading = false;
                // Don't set alert - preview might not exist for all sources
                console.warn('Unable to retrieve source preview:', action.error);
            })
            .addCase(editSource.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(editSource.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.managedSource = action.payload;
                }
                state.successMessage = 'Successfully edit source';
            })
            .addCase(editSource.rejected, (state, action) => {
                state.loading = false;
                if (action.error && action.error.message) {
                    state.alertMessage = action.error.message;
                } else {
                    state.alertMessage = 'Unable to edit an source';
                }
            })
            .addCase(createSource.pending, (state, action) => {
                state.loading = true;
                state.successMessage = undefined;
                state.alertMessage = undefined;
            })
            .addCase(createSource.fulfilled, (state, action) => {
                state.loading = false;
                state.reload = true;
                state.lastReload = new Date();
                state.successMessage = `Successfully create a new source [${action.payload.name} - ${action.payload.id}]`;
            })
            .addCase(createSource.rejected, (state, action) => {
                state.loading = false;
                if (action.error && action.error.message) {
                    state.alertMessage = action.error.message;
                } else {
                    console.error(`There was an unhandled rejection`, action);
                }
            })
            .addCase(getInputSchemas.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(getInputSchemas.fulfilled, (state, action) => {
                state.loading = false;
                state.inputSchemas = action.payload;
            })
            .addCase(getInputSchemas.rejected, (state, action) => {
                state.loading = false;
                if (action.error && action.error.message) {
                    state.alertMessage = action.error.message;
                } else {
                    console.error(`There was an unhandled rejection`, action);
                }
            })
            .addCase(browseS3ForSourceData.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(browseS3ForSourceData.fulfilled, (state, action) => {
                state.loading = false;
                state.s3Files = action.payload;
            })
            .addCase(browseS3ForSourceData.rejected, (state, action) => {
                state.loading = false;
                if (action.error && action.error.message) {
                    state.alertMessage = action.error.message;
                } else {
                    console.error(`There was an unhandled rejection`, action);
                }
            })
            .addCase(previewDataOnS3ForSourceData.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(previewDataOnS3ForSourceData.fulfilled, (state, action) => {
                state.loading = false;
                state.previewFromS3 = action.payload;
            })
            .addCase(previewDataOnS3ForSourceData.rejected, (state, action) => {
                state.loading = false;
                if (action.error && action.error.message) {
                    state.alertMessage = action.error.message;
                } else {
                    console.error(`There was an unhandled rejection`, action);
                }
            });
    }
});

export const {
    clearState,
    clearS3Files,
    clearUploadFiles,
    updateAllMappedToAttributes,
    updateMappedToAttributes,
    updateAllMappedSelectedFileFields,
    updateMappedSelectedFileFields,
} = sourceSlice.actions;

export default sourceSlice.reducer;
